import { useSidebar } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { Bookmark } from 'lucide-react';
import { useState } from 'react';
import { useToggleFavoriteQuestion } from '../../../../../hooks/useFavoriteQuestions';

export interface CardProps {
    readonly item: {
        readonly id: string;
        readonly title: string;
        readonly icon?: string;
        readonly isFavorite?: boolean;
        readonly interactionId?: string;
    };
    readonly className?: string;
    readonly setChatInputValue: React.Dispatch<React.SetStateAction<string>>;
    readonly defaultQuestions: readonly string[];
    readonly sendMessage: (msg: string) => void;
}

export function Card({ item, className, setChatInputValue, sendMessage, defaultQuestions }: CardProps) {
    const { state } = useSidebar();
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const currentOrganization = userInfo?.currentOrganization;

    const toggleFavorite = useToggleFavoriteQuestion();
    const [isLoading, setIsLoading] = useState(false);

    const handleToggleFavorite = async (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!userInfo?.id || !currentOrganization || !item.title.trim()) return;

        setIsLoading(true);
        try {
            await toggleFavorite.mutateAsync({
                questionText: item.title,
                userId: userInfo.id,
                organizationId: currentOrganization,
                interactionId: item.interactionId,
            });
        } catch (error) {
            console.error('Error toggling favorite:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <button
            key={item.id}
            className={`
                group flex flex-col items-center gap-5 cursor-pointer p-5 border-2 rounded-lg transition relative
                ${state === 'expanded' ? 'md:flex-col lg:flex-row' : 'md:flex-row '}
                bg-gray-qm-50 border-gray-qm-100 hover:bg-primary hover:border-primary hover:text-primary-foreground
                dark:bg-gray-qm-900 dark:border-tertiary-qm-300 dark:hover:bg-primary dark:hover:border-primary
                ${className}
            `}
            onClick={() => {
                setChatInputValue(item.title);
                setTimeout(() => {
                    sendMessage(item.title);
                }, 500);
            }}
        >
            {item.isFavorite !== undefined && !defaultQuestions.includes(item.title) && (
                <button
                    onClick={handleToggleFavorite}
                    disabled={isLoading}
                    className={`
                        absolute top-2 right-2 p-1 rounded-full transition-all duration-200 z-10
                        ${
                            item.isFavorite
                                ? 'opacity-100 text-yellow-500'
                                : 'opacity-100 text-gray-400 hover:text-yellow-500'
                        }
                        hover:bg-gray-200 dark:hover:bg-gray-700
                        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    title={item.isFavorite ? 'Remover de favoritos' : 'Agregar a favoritos'}
                    data-testid="card-favorite-button"
                >
                    <Bookmark
                        size={14}
                        className={`transition-all duration-200 ${item.isFavorite ? 'fill-current' : ''}`}
                    />
                </button>
            )}
            {item.icon && (
                <div
                    className={`
                bg-tertiary-qm-500 group-hover:bg-tertiary-qm-700 transition rounded-full h-12 w-12 min-w-12 flex items-center justify-center
                ${
                    state === 'expanded'
                        ? 'md:h-12 md:w-12 md:min-w-12 lg:h-16 lg:w-16 lg:min-w-16'
                        : 'md:h-16 md:w-16 md:min-w-16'
                }
            `}
                >
                    <img
                        className={`
                    h-6 ${state === 'expanded' ? 'md:h-6 lg:h-8' : 'md:h-8'}
                `}
                        src={item.icon}
                        alt={item.title}
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (item.icon && target.src !== `./assets/img/cards/${item.id}.svg`) {
                                target.src = `./assets/img/cards/${item.id}.svg`;
                            }
                        }}
                    />
                </div>
            )}
            <p className="font-poppins font-semibold leading-6 text-left" data-testid={`${item.id}-card`}>
                {item.title.charAt(0).toUpperCase() + item.title.slice(1)}
            </p>
        </button>
    );
}

export default Card;
